import store from '@/store/store'
import * as amplitude from '@amplitude/analytics-browser'
import { canUseStatistics, canUseMarketing } from './consent'
import { normalizeAnalyticsEventName } from './analyticsEventNaming'
import { getSubscriptionAnalyticsTier, normalizeSignupPremiumEventPayload } from './signupPremiumAnalytics'

const SAMPLE_EVENT = [
  //'Selected tool','Magic Heal'
]

/**
 * Tracks custom events using the Amplitude analytics library.
 * If the user is a sample user and the event is included in the SAMPLE_EVENT array,
 * the event will be tracked. Otherwise, the event will only be tracked if it is not
 * included in the SAMPLE_EVENT array.
 * 
 * Respects Cookiebot consent (statistics or marketing) unless marked as necessary.
 *
 * @param event - The name of the event to track.
 * @param data - Optional data to include with the event.
 * @param options - Optional settings:
 *   - necessary: track regardless of consent.
 *   - emitLegacyAliases: when true, dual-emits legacy alias names.
 *   - skipNormalization: bypass event/payload normalization.
 */

export function customAmplitude(
  event: string,
  data?: any,
  options?: { necessary?: boolean; emitLegacyAliases?: boolean; skipNormalization?: boolean },
) {
  const hasConsent = canUseStatistics() || canUseMarketing()
  const normalizedEventName = options?.skipNormalization
    ? { canonicalEvent: event, aliases: [] as string[], usedAliasInput: false }
    : normalizeAnalyticsEventName(event)
  const canonicalEvent = normalizedEventName.canonicalEvent
  const userState = store.getState().editor.user
  const payload = options?.skipNormalization
    ? data
    : normalizeSignupPremiumEventPayload(canonicalEvent, data,
      getSubscriptionAnalyticsTier(userState))

  const shouldEmitLegacyAliases =
    options?.emitLegacyAliases ?? process.env.REACT_APP_ANALYTICS_DUAL_EMIT_ALIASES === '1'

  const track = () => {
    const normalizedPayload =
      normalizedEventName.usedAliasInput && payload && typeof payload === 'object'
        ? { ...payload, legacy_event_name: event }
        : payload

    normalizedPayload
      ? amplitude.track(canonicalEvent, normalizedPayload)
      : amplitude.track(canonicalEvent)

    if (!shouldEmitLegacyAliases) return
    if (!normalizedEventName.aliases.length) return

    normalizedEventName.aliases.forEach((legacyAlias) => {
      amplitude.track(legacyAlias, {
        ...(normalizedPayload || {}),
        canonical_event_name: canonicalEvent,
      })
    })
  }
  
  // Handle necessary events - temporarily enable tracking if needed
  if (options?.necessary && !hasConsent) {
    amplitude.setOptOut(false)
    track()
    amplitude.setOptOut(true)
    return
  }
  
  // Skip non-necessary events if no consent
  if (!hasConsent) {
    return
  }

  const isSampleUser = userState.isSampleUser
  const isSelectedToolEvent = canonicalEvent === 'Selected tool'  
  // @ts-ignore
  isSelectedToolEvent && window.dataLayer.push({event: 'Selected tool', data: payload})
  if (SAMPLE_EVENT.includes(canonicalEvent)) {
    if(isSampleUser) {
      track()
    }
  } else {
    track()
  }
}
